import React from "react";
import PropTypes from "prop-types";

/**
 * ErrorFallback component provides a user-friendly UI when a React error occurs
 * It displays a message to the user and provides options to refresh the page or go back
 */
function ErrorFallback({ error, componentStack, resetError }) {
  return (
    <div className="min-h-screen bg-white px-4 py-16 sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">
      <div className="mx-auto max-w-max">
        <main className="sm:flex">
          <p className="text-4xl font-bold tracking-tight text-red-600 sm:text-5xl">Oops!</p>
          <div className="sm:ml-6">
            <div className="sm:border-l sm:border-gray-200 sm:pl-6">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">Something went wrong</h1>
              <p className="mt-1 text-base text-gray-500">
                We&#39;ve encountered an error and our team has been notified.
              </p>
              {process.env.NODE_ENV === "development" && (
                <div className="mt-3 max-h-40 overflow-auto rounded-md bg-gray-100 p-4">
                  <p className="font-mono text-sm text-gray-800">{error.toString()}</p>
                  {componentStack && <pre className="mt-2 font-mono text-xs text-gray-700">{componentStack}</pre>}
                </div>
              )}
            </div>
            <div className="mt-10 flex space-x-3 sm:border-l sm:border-transparent sm:pl-6">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Refresh page
              </button>
              {resetError && (
                <button
                  type="button"
                  onClick={resetError}
                  className="inline-flex items-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Try again
                </button>
              )}
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex items-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Go back
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

ErrorFallback.propTypes = {
  error: PropTypes.object.isRequired,
  componentStack: PropTypes.string,
  resetError: PropTypes.func,
};

export default ErrorFallback;
